import { useState } from "react";
import {
  Checkbox,
  Combobox,
  FormModal,
  Input,
  SegmentedControl,
  Select,
  Textarea,
} from "@marekl/adminer/components";
import { notifySuccess, notifyWarning } from "@marekl/adminer/api";
import { useSenderList, type Sender } from "../../lib/queries/senders";
import {
  useTemplateDetail,
  useTemplateList,
  type Template,
} from "../../lib/queries/templates";
import {
  useAdminSendMessage,
  type AdminSendMessageInput,
} from "../../lib/queries/messages";
import { isPhoneNumber, smsHint } from "../../lib/sms";
import styles from "./MessagesPage.module.css";

type ContentSource = "raw" | "template";
type Category = AdminSendMessageInput["category"];
type Channel = "email" | "sms";

const SOURCE_OPTIONS: { value: ContentSource; label: string }[] = [
  { value: "raw", label: "Write content" },
  { value: "template", label: "From template" },
];

const CHANNEL_OPTIONS: { value: Channel; label: string }[] = [
  { value: "email", label: "E-mail" },
  { value: "sms", label: "SMS" },
];

interface ComposeMessageModalProps {
  onClose: () => void;
  /** Receives the new message id once it is enqueued. */
  onSent: (id: string) => void;
}

/**
 * Manual compose: one message to one recipient, either hand-written
 * (subject + HTML/text) or rendered from a published template. It goes
 * through the same enqueue pipeline as the API — suppression, List-Unsubscribe
 * on marketing, text autoconversion — so it doubles as an end-to-end check.
 */
export function ComposeMessageModal({ onClose, onSent }: ComposeMessageModalProps) {
  const senders = useSenderList();
  const templates = useTemplateList();
  const sendMessage = useAdminSendMessage();

  const [channel, setChannel] = useState<Channel>("email");
  const [sender, setSender] = useState<string | null>(null);
  const [category, setCategory] = useState<Category>("transactional");
  const [to, setTo] = useState("");
  const [toName, setToName] = useState("");
  const [source, setSource] = useState<ContentSource>("raw");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [subjectOverride, setSubjectOverride] = useState("");
  // null = untouched: the textarea shows the picked template's sample data.
  const [data, setData] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [isTest, setIsTest] = useState(true);

  const isSMS = channel === "sms";
  const senderOptions = (senders.data ?? []).filter(
    (s) => s.active && s.channel === channel,
  );
  // Enqueue renders the current published version, so drafts can't be sent.
  const templateOptions = (templates.data ?? []).filter(
    (t) => t.active && t.has_published && t.channel === channel,
  );
  const picked = templateOptions.find((t) => t.key === templateKey);
  const detail = useTemplateDetail(picked?.id);
  const dataValue = data ?? detail.data?.sample_data_json ?? "";

  // An SMS is its body: no subject, no HTML, and the recipient is a number.
  const hasContent =
    source === "raw"
      ? isSMS
        ? !!text.trim()
        : !!subject.trim() && (!!html.trim() || !!text.trim())
      : !!templateKey;
  const recipientOk = isSMS ? isPhoneNumber(to) : !!to.trim();
  const canSubmit = !!sender && recipientOk && hasContent;

  /** Switching channel invalidates the sender and template picked for the other one. */
  function pickChannel(next: Channel) {
    setChannel(next);
    setSender(null);
    pickTemplate(null);
  }

  function pickTemplate(key: string | null) {
    setTemplateKey(key);
    setData(null);
    setDataError(null);
  }

  async function send() {
    const base = {
      category,
      sender: sender!,
      // The name field is hidden for SMS, but hidden is not the same as
      // cleared: switching channel mid-compose would otherwise send a name
      // typed for an e-mail, and the message detail then renders an SMS as
      // `Jane <+420777123456>`.
      to: {
        address: to.trim(),
        name: isSMS ? undefined : toName.trim() || undefined,
      },
      is_test: isTest,
    };
    let input: AdminSendMessageInput;
    if (source === "raw") {
      input = isSMS
        ? { ...base, text }
        : {
            ...base,
            subject: subject.trim(),
            html: html.trim() ? html : undefined,
            text: text.trim() ? text : undefined,
          };
    } else {
      let templateData: unknown;
      const trimmed = dataValue.trim();
      if (trimmed !== "") {
        try {
          templateData = JSON.parse(trimmed);
        } catch {
          setDataError("Invalid JSON.");
          return;
        }
      }
      setDataError(null);
      input = {
        ...base,
        template: templateKey!,
        subject: isSMS ? undefined : subjectOverride.trim() || undefined,
        template_data: templateData,
      };
    }
    try {
      const res = await sendMessage.mutateAsync(input);
      if (res.state === "suppressed") {
        notifyWarning(
          "The recipient is suppressed — the message was recorded but will not be sent.",
        );
      } else {
        notifySuccess("Message queued.");
      }
      if (res.warning) notifyWarning(res.warning);
      onSent(res.id);
    } catch {
      // Shown by the FormModal error slot (the mutation suppresses the toast).
    }
  }

  return (
    <FormModal
      open
      title="New message"
      width={640}
      onClose={onClose}
      busy={sendMessage.isPending}
      canSubmit={canSubmit}
      error={sendMessage.error ?? undefined}
      submitLabel="Send"
      onSubmit={() => void send()}
    >
      <SegmentedControl<Channel>
        label="Channel"
        options={CHANNEL_OPTIONS}
        value={channel}
        onChange={pickChannel}
      />
      <Combobox<Sender>
        label="Sender"
        options={senderOptions}
        value={sender}
        onChange={setSender}
        getOptionLabel={(s) =>
          s.from_address ? `${s.name} <${s.from_address}>` : s.name
        }
        getOptionValue={(s) => s.key}
        getOptionKey={(s) => s.id}
        placeholder={isSMS ? "Select an SMS sender…" : "Select a sender…"}
        loading={senders.isLoading}
        required
      />
      <Input
        label={isSMS ? "Phone number" : "Recipient"}
        type={isSMS ? "tel" : "email"}
        required
        value={to}
        onChange={(e) => setTo(e.target.value)}
        placeholder={isSMS ? "+420777123456" : "jane@example.com"}
        hint={
          isSMS
            ? "In E.164 form. Spaces, dashes and a leading 00 are accepted."
            : undefined
        }
        error={
          isSMS && to.trim() !== "" && !isPhoneNumber(to)
            ? "Not a phone number letterman can send to."
            : undefined
        }
        autoComplete="off"
      />
      {!isSMS && (
        <Input
          label="Recipient name (optional)"
          value={toName}
          onChange={(e) => setToName(e.target.value)}
          autoComplete="off"
        />
      )}
      <Select
        label="Category"
        value={category}
        onChange={(e) => setCategory(e.target.value as Category)}
      >
        <option value="transactional">Transactional</option>
        <option value="marketing">
          {isSMS
            ? "Marketing (wider suppression scope)"
            : "Marketing (adds unsubscribe headers)"}
        </option>
      </Select>
      <SegmentedControl<ContentSource>
        label="Content source"
        options={SOURCE_OPTIONS}
        value={source}
        onChange={setSource}
      />
      {source === "raw" ? (
        isSMS ? (
          <Textarea
            label="Message"
            rows={5}
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            hint={
              text === ""
                ? "160 characters fit one segment — 70 if any character is outside GSM-7."
                : smsHint(text)
            }
          />
        ) : (
          <>
            <Input
              label="Subject"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <Textarea
              label="HTML body"
              rows={10}
              spellCheck={false}
              className={styles.codeTextarea}
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              hint="Sent as-is — use email-safe markup (tables, inline styles)."
            />
            <Textarea
              label="Text body (optional)"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              hint="Leave empty to generate it from the HTML."
            />
          </>
        )
      ) : (
        <>
          <Combobox<Template>
            label="Template"
            options={templateOptions}
            value={templateKey}
            onChange={pickTemplate}
            getOptionLabel={(t) => `${t.name} (${t.key})`}
            getOptionValue={(t) => t.key}
            getOptionKey={(t) => t.id}
            placeholder="Select a published template…"
            loading={templates.isLoading}
            required
          />
          {!isSMS && (
            <Input
              label="Subject override (optional)"
              value={subjectOverride}
              onChange={(e) => setSubjectOverride(e.target.value)}
            />
          )}
          <Textarea
            label="Data (JSON, optional)"
            rows={8}
            spellCheck={false}
            className={styles.codeTextarea}
            value={dataValue}
            onChange={(e) => setData(e.target.value)}
            error={dataError ?? undefined}
            hint="Prefilled with the template’s sample data."
          />
        </>
      )}
      <Checkbox
        label="Mark as test message"
        checked={isTest}
        onChange={(e) => setIsTest(e.target.checked)}
      />
    </FormModal>
  );
}
